import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Mail, RefreshCw } from 'lucide-react';

export default function VerifyEmail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { verifyEmail, resendVerification } = useAuth();
  
  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setLoading(true);

    try {
      await verifyEmail(email, code);
      toast.success('Email verified! Welcome to Rbx.Casino!');
      navigate('/app');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setResending(true);

    try {
      await resendVerification(email);
      toast.success('Verification code resent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#1e1b4b_0%,_#020617_100%)]" />
      
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="block text-center mb-8">
          <span className="font-display text-3xl font-bold">
            <span className="text-slate-300">RBX</span>
            <span className="text-red-500">CASINO</span>
          </span>
        </Link>

        <div className="glass rounded-2xl p-8">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center">
              <Mail className="text-blue-400" size={32} />
            </div>
          </div>

          <h1 className="font-display text-2xl font-bold text-white text-center mb-2">
            Verify Your Email
          </h1>
          <p className="text-slate-400 text-center mb-8">
            We sent a 6-digit code to your email. Enter it below to verify your account.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!location.state?.email && (
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-default"
                  required
                  data-testid="verify-email-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="code" className="text-slate-300">Verification Code</Label>
              <Input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="input-default text-center text-2xl tracking-[0.5em] font-mono"
                maxLength={6}
                required
                data-testid="verify-code-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={loading || code.length !== 6}
              data-testid="verify-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify Email'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={handleResend}
              disabled={resending}
              className="text-slate-400 hover:text-blue-400 text-sm flex items-center justify-center gap-2 mx-auto transition-colors"
              data-testid="resend-code-btn"
            >
              {resending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw size={16} />
              )}
              Resend verification code
            </button>
          </div>

          <p className="text-center text-slate-400 mt-6">
            <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">
              Back to login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
